import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useCurrencyHelpers } from '@plato/app-shared/hooks/use-currency-helpers';
import { InsInsightOutcomeResponse, InsInsightOutcomeStatus } from '@plato/data/prisma';
import { lexicalToPlainText } from '@plato/node-util/rich-text/lexical-to-plain-text';
import { SearchEntity } from '@plato/search/search-types';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { Dialog, DialogContent } from '@plato/ui/containers/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { differenceInCalendarDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import type { InsightOutcomeNotRelevantFormValues } from '../../../components/forms/insight-outcome-not-relevant-form.tsx';
import { InsightOutcomeNotRelevantForm } from '../../../components/forms/insight-outcome-not-relevant-form.tsx';
import type { InsightOutcomeResolvedFormValues } from '../../../components/forms/insight-outcome-resolved-form.tsx';
import { InsightOutcomeResolvedForm } from '../../../components/forms/insight-outcome-resolved-form.tsx';
import type { CrmLinkModelType } from '../../../server/dto/crm-task-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useCustomerBasis } from '../../customer-detail/hooks/use-customer-basis.ts';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import type { SendAnalyticsEventHandler } from '../hooks/use-insights-with-analytics.ts';
import { isOpportunityInsightType } from '../lib/insight-bucket.ts';
import { getInsightEntityName } from '../lib/insight-entity-name.ts';
import { getInsightName } from '../lib/insight-name.ts';
import { InsightDialog } from './insight-dialog.tsx';
import { InsightReasonText } from './insight-reason-text.tsx';
import { InsightTypeInfoHint } from './insight-type-info-hint.tsx';
import { MoneyDeltaBadge } from './money-delta-badge.tsx';

export type InsightCardProps = {
    insightData: InsightListItem;
    onAnalyticsEvent: SendAnalyticsEventHandler;
    onVisibleChanged?: (isVisible: boolean) => void;
};

const insightLinkType: CrmLinkModelType = 'ai_insights';

export function InsightCard({
    insightData,
    onAnalyticsEvent,
    onVisibleChanged,
}: InsightCardProps): React.JSX.Element {
    const appConfig = useAppConfig();

    const { breadcrumbDepthsToHide } = appConfig;
    const { t } = useTranslation();

    const { insight, details } = insightData;
    // Without a category the backend creates no business rule, so the toast body must not
    // promise "{{category}} paused for N months" (RETAIN-813).
    const hasCategory = insight.actionable_entity_type === 'WEB_CATEGORY';
    const entityName = getInsightEntityName(t, insightData);
    const insightTypeName = getInsightName(t, insight, {
        neverBoughtCrossSell: appConfig.enableNeverBoughtCrossSellCopy,
    });

    const getOutcomeToastCopy = (response: InsInsightOutcomeResponse): { title: string; body: string } => {
        const bodyArgs = { category: entityName, recommendationType: insightTypeName };
        switch (response) {
            case InsInsightOutcomeResponse.BUYS_ELSEWHERE:
                return {
                    title: t('outcome_toast__buys_elsewhere__title', { ns: 'insights' }),
                    body: t('outcome_toast__buys_elsewhere__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.INCORRECT:
                return {
                    title: t('outcome_toast__incorrect__title', { ns: 'insights' }),
                    body: t('outcome_toast__incorrect__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.INTERESTED:
                return {
                    title: t('outcome_toast__interested__title', { ns: 'insights' }),
                    body: t('outcome_toast__interested__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.NO_NEED:
                return {
                    title: t('outcome_toast__no_need__title', { ns: 'insights' }),
                    body: t('outcome_toast__no_need__body', { ns: 'insights', ...bodyArgs }),
                };
            case InsInsightOutcomeResponse.NORMAL:
                return {
                    title: t('outcome_toast__normal__title', { ns: 'insights' }),
                    body: t('outcome_toast__normal__body', { ns: 'insights', ...bodyArgs }),
                };
        }
    };
    const daysSinceFirstSeen = insight.first_seen_at
        ? differenceInCalendarDays(new Date(), insight.first_seen_at)
        : undefined;
    const isNewInsight = daysSinceFirstSeen !== undefined && daysSinceFirstSeen <= 7;
    const newBadgeTooltip =
        daysSinceFirstSeen !== undefined && daysSinceFirstSeen <= 0
            ? t('badge__new_tooltip_today', { ns: 'insights' })
            : t('badge__new_tooltip_days_ago', { ns: 'insights', count: daysSinceFirstSeen });

    const filteredBreadcrumbs = details.category?.breadcrumbs?.slice(breadcrumbDepthsToHide) || [];
    const breadcrumbPaths = filteredBreadcrumbs.map((breadcrumb) => breadcrumb.title).join(' / ');

    const { data: customerBasics } = useCustomerBasis({ customerId: insight.customer_id });

    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
    const [outcomeStatus, setOutcomeStatus] = useState<InsInsightOutcomeStatus>(
        InsInsightOutcomeStatus.RESOLVED,
    );

    const openDetail = (): void => {
        setIsDetailOpen(true);
        onAnalyticsEvent({
            event: 'sales_insight_expanded',
            payload: {
                sales_insight_id: insight.id,
            },
        });
    };

    const showCreateTaskDialog = useShowCreateTaskDialog();

    const handleCreateTask = (): void => {
        showCreateTaskDialog({
            description: `${insightTypeName}: ${breadcrumbPaths ? `${breadcrumbPaths} / ${entityName}` : entityName}\n`,
            links: [
                {
                    modelId: insight.customer_id,
                    modelType: SearchEntity.Customer,
                    title: customerBasics?.customerName ?? '',
                },
                {
                    modelType: insightLinkType,
                    modelId: insight.id,
                    title: insightTypeName,
                },
            ],
            source: 'sales_insight',
        });
    };

    const { toCurrencyString } = useCurrencyHelpers();
    const estimatedPotential = toCurrencyString(roundAbsToHundred(insight.absolute_number ?? 0));

    const apiUtils = api.useUtils();
    const { toast } = useToast();
    const { mutate: deleteInsightOutcome } =
        api.platoUser.command.insights.postInsightActions.deleteInsightOutcome.useMutation({
            onSuccess: () => {
                void apiUtils.platoUser.query.insights.getManyInsights.getListWithFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.getManyCustomers.getListWithFilters.reset();
            },
        });

    // The outcome form is presentational; the card owns the submission, its success
    // toast (with undo) and the cache invalidation that follows.
    const { mutate: submitInsightOutcome, isPending: isSubmittingOutcome } =
        api.platoUser.command.insights.postInsightActions.submitInsightOutcome.useMutation({
            onSuccess: (data, variables) => {
                void apiUtils.platoUser.query.insights.getManyInsights.getListWithFilters.invalidate({
                    customerId: insight.customer_id,
                });
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                void apiUtils.platoUser.getManyCustomers.getListWithFilters.reset();

                const outcomeId = data.id;
                const outcomeToast = getOutcomeToastCopy(variables.response);
                const successToast = toast({
                    icon: 'success',
                    title: outcomeToast.title,
                    description: (
                        <>
                            {hasCategory
                                ? outcomeToast.body
                                : t('outcome_toast__saved_body', {
                                      ns: 'insights',
                                      category: entityName,
                                      recommendationType: insightTypeName,
                                  })}{' '}
                            <LinkText
                                as="button"
                                onClick={() => {
                                    successToast.dismiss();
                                    deleteInsightOutcome({ outcomeId });
                                }}
                            >
                                {t('outcome_toast__undo', { ns: 'insights' })}
                            </LinkText>
                        </>
                    ),
                });

                onAnalyticsEvent({
                    event:
                        variables.status === InsInsightOutcomeStatus.RESOLVED
                            ? 'sales_insight_completed'
                            : 'sales_insight_not_relevant',
                    payload: {
                        reason_given_dropdown: variables.response,
                        reasons_given_dropdown: variables.contextTags ?? [],
                        reason_given_text: lexicalToPlainText(variables.textRich ?? null) ?? '',
                        sales_insight_id: insight.id,
                    },
                });

                setIsOutcomeModalOpen(false);
            },
        });

    const handleSubmitResolved = (data: InsightOutcomeResolvedFormValues): void => {
        submitInsightOutcome({
            insightId: insight.id,
            status: InsInsightOutcomeStatus.RESOLVED,
            response: data.response,
            textRich: data.textRich ?? undefined,
        });
    };

    const handleSubmitNotRelevant = (data: InsightOutcomeNotRelevantFormValues): void => {
        submitInsightOutcome({
            insightId: insight.id,
            status: InsInsightOutcomeStatus.NOT_RELEVANT,
            response: data.response,
            textRich: data.textRich ?? undefined,
            contextTags: data.contextTags.length > 0 ? data.contextTags : undefined,
        });
    };

    const handleResolve = (): void => {
        setOutcomeStatus(InsInsightOutcomeStatus.RESOLVED);
        setIsOutcomeModalOpen(true);
    };

    const handleNotRelevant = (): void => {
        setOutcomeStatus(InsInsightOutcomeStatus.NOT_RELEVANT);
        setIsOutcomeModalOpen(true);
    };

    const { ref, inView } = useInView({
        onChange: (isVisible) => {
            onVisibleChanged?.(isVisible);
        },
        threshold: 0.8, // 80% of the component should be visible (to account for padding)
    });

    useEffect(
        () => {
            if (!inView) {
                return;
            }

            const handle = setTimeout(
                (insightId) => {
                    onAnalyticsEvent({
                        event: 'sales_insight_viewed',
                        payload: {
                            sales_insight_id: insightId,
                        },
                    });
                },
                3000,
                insight.id,
            );

            return () => {
                clearTimeout(handle);
            };
        },
        // don't include onAnalyticsEvent in deps so we don't have to memo it in the parent
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inView, insight.id],
    );

    return (
        <div ref={ref}>
            <Dialog
                open={isOutcomeModalOpen}
                onClose={() => {
                    setIsOutcomeModalOpen(false);
                }}
            >
                <DialogContent className="sm:max-w-2xl">
                    {outcomeStatus === InsInsightOutcomeStatus.RESOLVED ? (
                        <InsightOutcomeResolvedForm
                            insight={insight}
                            estimatedPotential={estimatedPotential}
                            productCategoryTitle={entityName}
                            isSubmitting={isSubmittingOutcome}
                            onSubmit={handleSubmitResolved}
                            onClose={() => {
                                setIsOutcomeModalOpen(false);
                            }}
                        />
                    ) : (
                        <InsightOutcomeNotRelevantForm
                            insight={insight}
                            estimatedPotential={estimatedPotential}
                            productCategoryTitle={entityName}
                            isSubmitting={isSubmittingOutcome}
                            onSubmit={handleSubmitNotRelevant}
                            onClose={() => {
                                setIsOutcomeModalOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <InsightDialog
                open={isDetailOpen}
                onClose={() => {
                    setIsDetailOpen(false);
                }}
                insightData={insightData}
                onCreateTask={handleCreateTask}
                onResolve={handleResolve}
                onNotRelevant={handleNotRelevant}
                onAnalyticsEvent={onAnalyticsEvent}
            />
            <div className="sr-only">{insight.id}</div>
            <Card role="article">
                <CardFront>
                    <CardContent className="min-w-0">
                        <CardHeader className="flex-row flex-nowrap items-center gap-cozy">
                            <CardTitle data-testid="insight-type" className="min-w-0 grow-0 font-semibold">
                                {insightTypeName}
                            </CardTitle>
                            {isNewInsight && (
                                <Tooltip>
                                    <TooltipTrigger
                                        render={
                                            <button
                                                type="button"
                                                className="inline-flex shrink-0 cursor-help items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-600"
                                            />
                                        }
                                    >
                                        {t('badge__new', { ns: 'insights' })}
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{newBadgeTooltip}</TooltipContent>
                                </Tooltip>
                            )}
                            <CardSubtitle className="order-none ml-auto shrink-0 items-center tabular-nums">
                                <span data-testid="insight-revenue">
                                    <MoneyDeltaBadge
                                        className="font-bold"
                                        upToLabel={
                                            appConfig.enableUpToPotentialLabel &&
                                            isOpportunityInsightType(insight.insight_type)
                                        }
                                    >
                                        {roundAbsToHundred(insight.absolute_number ?? 0)}
                                    </MoneyDeltaBadge>
                                </span>
                                <InsightTypeInfoHint insightData={insightData} />
                            </CardSubtitle>
                        </CardHeader>
                        <CardDetail data-testid="insight-category" className="text-content">
                            {entityName}
                        </CardDetail>
                        {breadcrumbPaths && (
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <button
                                            type="button"
                                            className="self-start max-w-full min-w-0 cursor-help overflow-hidden text-ellipsis whitespace-nowrap [direction:rtl] text-left text-content-weak"
                                        />
                                    }
                                >
                                    {breadcrumbPaths}
                                </TooltipTrigger>
                                <TooltipContent
                                    side="bottom"
                                    dir="ltr"
                                    className="max-w-[min(420px,calc(100vw-8px))] break-words"
                                >
                                    {breadcrumbPaths}
                                </TooltipContent>
                            </Tooltip>
                        )}
                        <CardDetail>
                            <InsightReasonText insight={insight} categoryName={entityName} />
                        </CardDetail>
                    </CardContent>
                    <CardMainAction
                        aria-label={t('show_details', { ns: 'common' })}
                        onClick={openDetail}
                        className="absolute inset-0"
                    />
                </CardFront>
            </Card>
        </div>
    );
}
